import React from "react";
import { Code } from "lucide-react";

interface CameraDynamicDetailsProps {
    data: any;
}

const formatKey = (key: string) => {
    return key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, c => c.toUpperCase());
};

const DynamicDetailsRenderer = ({ data, isRoot = false }: { data: any; isRoot?: boolean }) => {
    if (data === null || data === undefined) {
        return <span className="text-slate-400 font-mono text-[10px]">null</span>;
    }

    if (typeof data !== "object") {
        let displayValue = String(data);
        let colorClass = "text-slate-700";

        if (typeof data === "boolean") {
            displayValue = data ? "Yes" : "No";
            colorClass = data ? "text-emerald-600" : "text-rose-600";
        } else if (typeof data === "number") {
            colorClass = "text-blue-600";
        }

        return <span className={`${colorClass} font-mono text-[10px] break-all`}>{displayValue}</span>;
    }

    if (Array.isArray(data)) {
        if (data.length === 0) return <span className="text-slate-400 font-mono text-[10px]">Empty Array</span>;
        if (data.every(item => typeof item !== 'object' && item !== null)) {
            return (
                <div className="flex flex-wrap gap-1 mt-1">
                    {data.map((item, idx) => (
                        <span key={idx} className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 text-slate-600 rounded text-[9px] font-mono">
                            {String(item)}
                        </span>
                    ))}
                </div>
            );
        }
        return (
            <div className="mt-1 space-y-1">
                {data.map((item, idx) => (
                    <div key={idx} className="bg-slate-50/50 border border-slate-100 rounded pl-2 pr-1 py-1 border-l-2 border-l-[#422568]/30">
                        <DynamicDetailsRenderer data={item} />
                    </div>
                ))}
            </div>
        );
    }

    const entries = Object.entries(data).filter(([key]) => {
        const k = key.toLowerCase();
        return k !== 'snapshot_uris' && k !== 'snapshoturis';
    });

    if (entries.length === 0) return null;

    if (isRoot) {
        return (
            <div className="columns-1 md:columns-2 lg:columns-3 gap-3 space-y-3">
                {entries.map(([key, val]) => (
                    <div key={key} className="bg-white rounded-lg border border-slate-200 overflow-hidden shadow-sm break-inside-avoid w-full">
                        <div className="px-3 py-1.5 border-b border-white bg-slate-50/50 flex items-center gap-2">
                            <Code className="w-3.5 h-3.5 text-[#422568]" />
                            <h3 className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{formatKey(key)}</h3>
                        </div>
                        <div className="p-2.5 overflow-x-auto">
                            <DynamicDetailsRenderer data={val} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-1 w-full">
            {entries.map(([key, val]) => {
                const isComplex = typeof val === 'object' && val !== null;
                return (
                    <div key={key} className="flex flex-col text-[10px]">
                        <div className="flex items-start justify-between py-1 border-b border-slate-100/50">
                            <span className="text-slate-400 font-bold tracking-tight pr-4 shrink-0 uppercase text-[8px] mt-0.5 text-left">
                                {formatKey(key)}
                            </span>
                            <div className="flex-1 text-right min-w-0">
                                {isComplex ? (
                                    <div className="text-left mt-1 w-full bg-slate-50/30 rounded p-1.5 border border-slate-100/50">
                                        <DynamicDetailsRenderer data={val} />
                                    </div>
                                ) : (
                                    <DynamicDetailsRenderer data={val} />
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const CameraDynamicDetails: React.FC<CameraDynamicDetailsProps> = ({ data }) => {
    if (!data) return null;

    return (
        <div className="pt-2 pb-10">
            <div className="flex items-center gap-2 mb-3">
                <Code className="w-4 h-4 text-[#422568]" />
                <h2 className="text-[11px] font-black text-slate-900 uppercase tracking-widest leading-none">Extended Hardware Telemetry</h2>
                <div className="h-px bg-slate-200 flex-1 ml-2"></div>
            </div>
            <DynamicDetailsRenderer data={data} isRoot={true} />
        </div>
    );
};

export default CameraDynamicDetails;
